import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import { findDOMNode } from 'react-dom';

import ChevronDownIcon from '@ott/icon-chevron-down';

import InputTrigger from '@ott/input-trigger';
import Overlay from '@ott/overlay';

import { withLayoutContext } from '@ott/layout-context';

import cls from 'classnames/bind';

import _noop from 'lodash/noop';
import s from './Timepicker.scss';
import Timesheet from './blocks/Timesheet/Timesheet';

const cx = cls.bind(s);

@withLayoutContext
class Timepicker extends PureComponent {
  static propTypes = {
    input: PropTypes.object.isRequired,
    meta: PropTypes.object.isRequired,
    placeholder: PropTypes.node,
    overlayPlacement: PropTypes.string,
    onTimeSelect: PropTypes.func,
    minTime: PropTypes.string,
    maxTime: PropTypes.string,
    timesheet: PropTypes.array,
    noMinMax: PropTypes.bool,
    classNames: PropTypes.shape({
      inputTrigger: PropTypes.string,
      overlayContainer: PropTypes.string,
    }),
    isAbsolutePosition: PropTypes.bool,
    isRelativePosition: PropTypes.bool,
    withArrowIcon: PropTypes.bool,
  };

  static defaultProps = {
    onTimeSelect: _noop,
    placeholder: 'Время',
    overlayPlacement: 'right',
    classNames: {},
    isAbsolutePosition: true,
    isRelativePosition: false,
    withArrowIcon: true,
  };

  state = {
    isOpen: false,
  };

  componentDidUpdate() {
    if (this.state.isOpen) {
      this.bindOutsideClicks();
    } else {
      this.unbindOutsideClicks();
    }
  }

  componentWillUnmount() {
    clearTimeout(this._closeTO);
    this.unbindOutsideClicks();
  }

  bindOutsideClicks() {
    if (!this._bound) {
      const { isTouchDevice } = this.props;
      const eventName = isTouchDevice ? 'touchend' : 'click';

      document.addEventListener(eventName, this.handleClickOutside);
      this._bound = true;
    }
  }

  unbindOutsideClicks() {
    if (this._bound) {
      const { isTouchDevice } = this.props;
      const eventName = isTouchDevice ? 'touchend' : 'click';

      document.removeEventListener(eventName, this.handleClickOutside);
      this._bound = false;
    }
  }

  handleBubble = e => {
    if (e) {
      e.stopPropagation();
      if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
        e.nativeEvent.stopImmediatePropagation();
      }
    }
  };

  handleClick = e => {
    this.handleBubble(e);
    const triggerNode = findDOMNode(this.trigger);
    if (triggerNode.contains(e.target)) {
      if (this.state.isOpen && !this._preventCloseByTriggerClick) {
        this.handleCloseAsync(e);
      } else {
        this.handleOpen(e);
      }
    }
  };

  handleClickOutside = e => {
    const domNode = findDOMNode(this);

    if (!domNode || !domNode.contains(e.target)) {
      this.handleCloseAsync();
    }
  };

  handleKeyDown = e => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        this.handleOpen(e);
        break;
      case 'Escape':
        this.handleBlur(e);
        break;
      default:
      // do nothing
    }
  };

  handleFocus = e => {
    // кейс: инпут активен > клик по адресной строке > клик по инпуту (focus > click)
    // без этого привента handleClick будет скрывать дропдаун
    this._preventCloseByTriggerClick = true;
    setTimeout(() => (this._preventCloseByTriggerClick = false), 200);

    this.handleOpen(e);
  };

  handleBlur = () => {
    this.handleCloseAsync();
  };

  handleOpen = e => {
    clearTimeout(this._closeTO);
    this.props.input.onFocus(e);
    this.setState({ isOpen: true });
  };

  handleClose = e => {
    this.setState({
      isOpen: false,
    });

    this.props.input.onBlur(e);
  };

  handleCloseAsync = () => {
    this._closeTO = setTimeout(this.handleClose, 30);
  };

  handleSelect = time => {
    this.handleCloseAsync();
    this.props.onTimeSelect(time);
  };

  renderIcon() {
    const { isOpen } = this.state;
    const { withArrowIcon } = this.props;

    if (withArrowIcon) {
      return (
        <ChevronDownIcon
          className={cls(s.arrow, {
            [s.isOpen]: isOpen,
          })}
        />
      );
    }

    return null;
  }

  renderTriggerNode() {
    const {
      meta,
      input: { name, value },
      placeholder,
      classNames: { inputTrigger: inputTriggerCls },
    } = this.props;

    const isInvalid = meta.invalid && meta.submitFailed;

    const placeholderNode = !value && (
      <span
        className={cx('placeholder', {
          'placeholder--dim': !isInvalid,
          'placeholder--warn': isInvalid,
        })}
      >
        {placeholder}
      </span>
    );

    return (
      <InputTrigger
        isActive={this.state.isOpen}
        isInvalid={isInvalid}
        iconNode={this.renderIcon()}
        name={name}
        ref={this.setTriggerRef}
        className={inputTriggerCls}
      >
        <div className={cx('trigger')}>
          {placeholderNode}
          {value}
        </div>
      </InputTrigger>
    );
  }

  linkContainer = () => findDOMNode(this);

  setTriggerRef = trigger => {
    this.trigger = trigger;
  };

  renderOverlayNode() {
    if (!this.state.isOpen) {
      return null;
    }

    const {
      overlayPlacement,
      input,
      minTime,
      maxTime,
      timesheet,
      noMinMax,
      isRelativePosition,
      isAbsolutePosition,
      classNames: { overlayContainer: overlayContainerClass },
    } = this.props;

    return (
      <Overlay
        isAbsolutePosition={isAbsolutePosition && !isRelativePosition}
        isRelativePosition={isRelativePosition}
        show={true}
        container={this.linkContainer}
        placement={overlayPlacement}
      >
        <div
          className={cx('container', {
            [overlayContainerClass]: overlayContainerClass,
          })}
          tabIndex="-1"
          onClick={this.handleBubble}
        >
          <div className={cx('container-timesheet')}>
            <Timesheet
              timesheet={timesheet}
              noMinMax={noMinMax}
              minTime={minTime}
              maxTime={maxTime}
              onTimeSelect={this.handleSelect}
              selectedTime={input.value}
            />
          </div>
        </div>
      </Overlay>
    );
  }

  render() {
    return (
      <div
        className={cx('Timepicker')}
        tabIndex="0"
        onMouseDown={this.handleClick}
        onKeyDown={this.handleKeyDown}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
      >
        {this.renderTriggerNode()}
        {this.renderOverlayNode()}
      </div>
    );
  }
}

export default Timepicker;
